/**
 * Sub1G703Billing — G703 grid (wizard wrapper + reusable PayAppG703Grid)
 */

import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { fetchPayAppDetail, updatePayAppLines } from '../../../../api/projects'
import type { WizardInProgress } from '../../../../api/types'
import {
  applyBillingPctToAll,
  applyCoLineBillingDefaults,
  applyDefaultRetainageToAll,
  applyBalanceToBillAll,
  balanceToBillPctForLine,
  normalizePctString,
  allLinesFullyBilled,
  anyLineExceeds100,
  buildLinesSavePayload,
  ensureBillingDefaults,
  lineExceeds100,
  maxRetainagePctFromPayload,
  overbillLineMessage,
  RETAINAGE_WARN_THRESHOLD,
  toDraftSlice,
  toEditableLine,
  updateEditableLine,
  type G703EditableLine,
} from '../../../../lib/g703GridLogic'
import { clearG703Draft, loadG703Draft, saveG703Draft } from '../../../../lib/payAppGridDrafts'

export interface PayAppG703SaveTotals {
  amountDue: number
  retainage: number
}

export interface PayAppG703GridProps {
  payAppId: number
  onSaved: (totals: PayAppG703SaveTotals) => void
  embed?: boolean
  onBack?: () => void
  backLabel?: string
  draftLines?: Array<{ id: number; _thisPct: string; _retPct: string; _retTouched?: boolean }>
  onDraftLinesChange?: (draft: Array<{ id: number; _thisPct: string; _retPct: string; _retTouched?: boolean }>) => void
  /** Shown when balance-to-bill fills every line to 100% */
  onAllLinesFullyBilled?: () => void
  retentionHeld?: number | null
  onBillRetention?: () => void
  billRetentionBusy?: boolean
  /** Project default retainage % for "Apply to all lines" action */
  defaultRetainagePct?: number | string | null
  highlightRetCells?: boolean
  onValidityChange?: (valid: boolean) => void
}

function fmt(n: number) {
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function PayAppG703Grid({
  payAppId, onSaved, embed, onBack, backLabel = '← Back to Review SOV',
  draftLines, onDraftLinesChange, onAllLinesFullyBilled,
  retentionHeld, onBillRetention, billRetentionBusy, defaultRetainagePct = 5,
  highlightRetCells = false,
  onValidityChange,
}: PayAppG703GridProps) {
  const { data: paDetail, isLoading } = useQuery({
    queryKey: ['pay-app-detail', payAppId],
    queryFn:  () => fetchPayAppDetail(payAppId),
    staleTime: 0,
  })

  const [lines, setLines] = useState<G703EditableLine[]>([])
  const [bulkPct, setBulkPct] = useState('20')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [retentionWarn, setRetentionWarn] = useState<string | null>(null)
  const hydratedForId = useRef<number | null>(null)

  useEffect(() => {
    if (!paDetail?.lines) return
    if (hydratedForId.current === paDetail.id) return
    hydratedForId.current = paDetail.id
    const stored = loadG703Draft(payAppId)
    const draftMap = new Map((draftLines ?? stored ?? []).map(d => [d.id, d]))
    const mapped = paDetail.lines.map(l => {
      const draft = draftMap.get(l.id)
      return toEditableLine(l, draft)
    })
    const hasDraftOrServerPct = mapped.some(l => {
      const draft = draftMap.get(l.id)
      if (draft?._thisPct && parseFloat(draft._thisPct) > 0) return true
      return parseFloat(l.this_pct) > 0
    })
    const withCo = applyCoLineBillingDefaults(mapped)
    const withDefaults = hasDraftOrServerPct ? withCo : ensureBillingDefaults(withCo, bulkPct)
    setLines(withDefaults)
    const slice = withDefaults.map(toDraftSlice)
    if (onDraftLinesChange) onDraftLinesChange(slice)
    saveG703Draft(payAppId, slice)
  }, [paDetail, payAppId, draftLines, bulkPct, onDraftLinesChange])

  const pushDraft = (next: G703EditableLine[]) => {
    const slice = next.map(toDraftSlice)
    saveG703Draft(payAppId, slice)
    if (onDraftLinesChange) onDraftLinesChange(slice)
  }

  const updateLine = (idx: number, key: '_thisPct' | '_retPct', value: string) => {
    setLines(prev => {
      const next = updateEditableLine(prev, idx, key, value)
      pushDraft(next)
      return next
    })
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      // Save the grid exactly as displayed. Defaults are applied once at
      // hydration; re-applying here turned an intentional all-zero grid
      // (change-order-only pay app) back into the bulk default.
      const payload = buildLinesSavePayload(lines)
      return updatePayAppLines(payAppId, { lines: payload })
    },
    onSuccess: (data) => {
      clearG703Draft(payAppId)
      const serverDue = typeof data?.amount_due === 'number' ? data.amount_due : null
      const serverRet = typeof data?.retention_held === 'number' ? data.retention_held : null
      const earned = totThisPeriod - totRetainage
      onSaved({
        amountDue: serverDue ?? Math.max(0, earned),
        retainage: serverRet ?? totRetainage,
      })
    },
    onError: (err: Error) => setSubmitError(err.message),
  })

  const savePayload = buildLinesSavePayload(lines)
  const maxRetPct = maxRetainagePctFromPayload(savePayload)

  useEffect(() => {
    if (maxRetPct > RETAINAGE_WARN_THRESHOLD) {
      setRetentionWarn(`Retention is set to ${maxRetPct}% — is that right?`)
    } else {
      setRetentionWarn(null)
    }
  }, [maxRetPct])

  const totThisPeriod = lines.reduce((s, l) => {
    const sv = parseFloat(l.scheduled_value) || 0
    return s + sv * (parseFloat(l._thisPct) || 0) / 100
  }, 0)
  const totRetainage = lines.reduce((s, l) => {
    const sv = parseFloat(l.scheduled_value) || 0
    const pct = parseFloat(l._thisPct) || 0
    const retPct = l._retTouched
      ? (parseFloat(l._retPct) || 0)
      : (parseFloat(l._serverRetPct) || 0)
    return s + sv * pct / 100 * retPct / 100
  }, 0)
  const netDue = Math.max(0, totThisPeriod - totRetainage)
  const defaultRetLabel = normalizePctString(defaultRetainagePct)

  const totBalance = lines.reduce((s, l) => {
    const sv = parseFloat(l.scheduled_value) || 0
    const thisPd = sv * (parseFloat(l._thisPct) || 0) / 100
    const retPct = l._retTouched
      ? (parseFloat(l._retPct) || 0)
      : (parseFloat(l._serverRetPct) || 0)
    const ret = thisPd * retPct / 100
    const prevBilled = sv * (parseFloat(l.prev_pct) || 0) / 100
    return s + (sv - prevBilled - thisPd - ret)
  }, 0)

  const fullyBilled = allLinesFullyBilled(lines)
  const gridInvalid = anyLineExceeds100(lines)
  useEffect(() => {
    if (fullyBilled) onAllLinesFullyBilled?.()
  }, [fullyBilled, onAllLinesFullyBilled])
  useEffect(() => {
    onValidityChange?.(!gridInvalid)
  }, [gridInvalid, onValidityChange])

  const applyLineBalance = (idx: number) => {
    setLines(prev => {
      const next = prev.map((l, i) =>
        i === idx ? { ...l, _thisPct: balanceToBillPctForLine(l) } : l,
      )
      pushDraft(next)
      return next
    })
  }

  if (isLoading) {
    return <div style={{ color: 'var(--ink4)', fontSize: 13 }}>Loading line items…</div>
  }

  return (
    <div data-testid={embed ? 'payapp-g703-embed' : 'wizard-step4-content'}>
      {!embed && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink)' }}>
                Pay Application #{paDetail?.app_number ?? '—'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink4)' }}>Billing grid · {paDetail?.project_name}</div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink4)', marginBottom: 14 }}>Continuation Sheet</div>
        </>
      )}

      <div className="panel" style={{ padding: '14px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink2)' }}>Apply billing % to all lines:</div>
        <input className="fld-i mono" value={bulkPct} onChange={e => setBulkPct(e.target.value)}
          onBlur={() => {
            const n = parseFloat(bulkPct)
            if (!Number.isFinite(n) || n < 0 || n > 100) return
            setLines(prev => {
              const next = applyBillingPctToAll(prev, bulkPct)
              pushDraft(next)
              return next
            })
          }}
          style={{ width: 60, padding: '5px 8px', textAlign: 'center', fontWeight: 600 }} />
        <span style={{ fontSize: 12, color: 'var(--ink2)' }}>%</span>
        <button type="button" className="btn b-dk btn-sm"
          onClick={() => setLines(prev => {
            const next = applyBillingPctToAll(prev, bulkPct)
            pushDraft(next)
            return next
          })}>Apply to all</button>
        <button type="button" className="btn b-ol btn-sm"
          onClick={() => setLines(prev => {
            const next = applyBillingPctToAll(prev, '0')
            pushDraft(next)
            return next
          })}>Clear all</button>
        <button type="button" className="btn b-grn btn-sm" data-testid="g703-balance-all"
          onClick={() => setLines(prev => {
            const next = applyBalanceToBillAll(prev)
            pushDraft(next)
            return next
          })} title="Set each line to bill its remaining balance (prior + this = 100%)">
          Balance to bill all
        </button>
        <button type="button" className="btn b-ol btn-sm" data-testid="g703-apply-default-retainage"
          title={`Set every line's Ret. % to the project default (${defaultRetLabel}%) without changing billing %`}
          onClick={() => setLines(prev => {
            const next = applyDefaultRetainageToAll(prev, defaultRetainagePct ?? 5)
            pushDraft(next)
            return next
          })}>
          Apply default retainage to all lines
        </button>
        <button type="button" className="btn b-dk btn-sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || gridInvalid}>
          {saveMutation.isPending ? 'Saving…' : 'Save & Continue →'}
        </button>
      </div>

      {fullyBilled && (retentionHeld ?? 0) > 0 && onBillRetention && (
        <div className="panel" style={{ padding: '12px 16px', marginBottom: 14, borderColor: 'var(--blue)' }}
          data-testid="g703-bill-retention-banner">
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 6 }}>
            Work is 100% billed — ready to release retainage
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 10 }}>
            Retainage held: ${fmt(retentionHeld ?? 0)} · One click prefills the final retention-release pay app.
          </div>
          <button type="button" className="btn b-dk btn-sm" disabled={billRetentionBusy}
            data-testid="g703-bill-retention-btn" onClick={onBillRetention}>
            {billRetentionBusy ? 'Creating…' : 'Bill retention'}
          </button>
        </div>
      )}

      {retentionWarn && (
        <p style={{ color: 'var(--amber)', fontSize: 12, marginBottom: 10 }} data-testid="g703-retention-warn">
          {retentionWarn}
        </p>
      )}

      <div className="panel" style={{ overflowX: 'auto' }}>
        <table className="tbl" style={{ minWidth: 700, fontSize: 11 }}>
          <thead>
            <tr>
              <th>Item</th><th>Description</th>
              <th className="r">Sched. Value</th>              <th className="r">% This</th>
              <th className="r"></th>
              <th className="r">Ret. %</th>
              <th className="r" title="Gross work billed this period before retainage is withheld. Changing Ret. % does not change this column.">
                Work this period (gross)
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((row, i) => {
              const sv = parseFloat(row.scheduled_value) || 0
              const thisPd = sv * (parseFloat(row._thisPct) || 0) / 100
              const prev = parseFloat(row.prev_pct) || 0
              const canBalance = sv > 0 && prev < 100
              return (
                <tr key={row.id}>
                  <td className="mono">{row.item_id || '—'}</td>
                  <td>{row.description}</td>
                  <td className="r mono-c">${fmt(sv)}</td>
                  <td className="r">
                    <input className="fld-i mono-c" value={row._thisPct}
                      data-testid={`g703-this-pct-${row.id}`}
                      onChange={e => updateLine(i, '_thisPct', e.target.value)}
                      style={{ padding: 4, fontSize: 10, width: 50, textAlign: 'right' }} />
                    {lineExceeds100(row) && (
                      <div
                        data-testid={`g703-overbill-${row.id}`}
                        style={{ color: 'var(--red)', fontSize: 10, marginTop: 2, maxWidth: 140 }}
                      >
                        {overbillLineMessage(row)}
                      </div>
                    )}
                  </td>
                  <td className="r">
                    {canBalance && (
                      <button type="button" className="btn b-ol btn-sm"
                        data-testid={`g703-balance-line-${row.id}`}
                        style={{ padding: '2px 6px', fontSize: 9 }}
                        title={`Bill remaining ${balanceToBillPctForLine(row)}% on this line`}
                        onClick={() => applyLineBalance(i)}>
                        Balance
                      </button>
                    )}
                  </td>
                  <td className="r">
                    <input
                      className={`fld-i mono-c${highlightRetCells ? ' g703-ret-highlight' : ''}`}
                      data-testid={`g703-ret-pct-${row.id}`}
                      value={row._retPct}
                      onChange={e => updateLine(i, '_retPct', e.target.value)}
                      style={{
                        padding: 4,
                        fontSize: 10,
                        width: 42,
                        textAlign: 'right',
                        ...(highlightRetCells ? { outline: '2px solid #eab308', background: '#fffbeb' } : {}),
                      }}
                    />
                  </td>
                  <td className="r mono-c">${fmt(thisPd)}</td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--bg)', fontWeight: 700 }}>
              <td colSpan={6} className="r">TOTALS</td>
              <td className="r mono-c">${fmt(totThisPeriod)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {submitError && <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 10 }}>{submitError}</p>}

      <div className="g703-sticky-footer" data-testid="g703-sticky-footer">
        {embed && (
          <div
            style={{ display: 'flex', gap: 16, fontSize: 12, fontWeight: 600, color: 'var(--ink2)' }}
            data-testid="g703-sticky-totals"
          >
            <span>Gross: <span className="mono-c">${fmt(totThisPeriod)}</span></span>
            <span>Retainage: <span className="mono-c">${fmt(totRetainage)}</span></span>
            <span>Net due: <span className="mono-c" data-testid="g703-net-due">${fmt(netDue)}</span></span>
          </div>
        )}
        {onBack && (
          <button type="button" className="btn b-ol" onClick={onBack}>{backLabel}</button>
        )}
        <button
          type="button"
          className="btn b-dk btn-lg"
          style={{ marginLeft: embed ? 'auto' : 'auto' }}
          data-testid="g703-save-continue-bottom"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || gridInvalid}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save & Continue →'}
        </button>
      </div>

      {!embed && (
        <div style={{ fontSize: 10, color: 'var(--ink4)', marginTop: 8 }}>
          Retainage total: ${fmt(totRetainage)} · Net due: ${fmt(netDue)} · Balance: ${fmt(totBalance)}
        </div>
      )}
    </div>
  )
}

interface WizardProps {
  wizard: WizardInProgress
  setWizard: (u: WizardInProgress | ((p: WizardInProgress) => WizardInProgress)) => void
  onBack: () => void
  onNext: () => void
}

export default function Sub1G703Billing({ wizard, setWizard, onBack, onNext }: WizardProps) {
  if (!wizard.payAppId) {
    return (
      <div className="wstep" data-testid="wizard-step4-content">
        <div style={{ color: 'var(--red)', fontSize: 13 }}>
          Pay app not found — go back to Period Info to create one.
        </div>
      </div>
    )
  }

  return (
    <div className="wstep">
      <PayAppG703Grid
        payAppId={wizard.payAppId}
        draftLines={wizard.step4G703Draft}
        defaultRetainagePct={wizard.step1.default_retainage ?? 5}
        onDraftLinesChange={(draft) => setWizard(w => ({ ...w, step4G703Draft: draft }))}
        onSaved={() => onNext()}
        onBack={onBack}
        backLabel="← Back to Period Info"
      />
    </div>
  )
}
